import type { AuthError, Session } from '@supabase/supabase-js';
import { Platform } from 'react-native';
import { markAsLoggedIn, saveLastEmail } from '../utils/firstTimeUser';
import { buildDashboardPath } from './dashboardRoutes';
import {
    appendQueryParams,
    FUNNEL_QUERY_KEYS,
    normalizePostAuthRedirect,
    type PartialFunnelContext,
    withFunnelAuthParams,
} from './funnelContext';
import { supabase } from './supabase';
import { resolveProfileCompletionDestination } from './profileCompletion';
import { msg } from '@lingui/core/macro';
import type { LocalizedMessage } from './i18n/messages';
import { getAuthMessageLocaleMetadata } from './clientSessionMetadata';
import { resumptionEntryDestination } from './onboardingResumptionContract';

export type PasswordlessEmailType = 'email' | 'email_change';
export type PasswordlessAuthType = PasswordlessEmailType | 'signup';

export interface PasswordlessConfirmationMessage {
    type: 'email_confirmed' | 'account_created';
    title: LocalizedMessage;
    message: LocalizedMessage;
}

export interface PasswordlessAuthOutcome {
    destination: string;
    needsProfileCompletion: boolean;
    confirmationMessage?: PasswordlessConfirmationMessage;
    trackingProjectId?: string;
}

interface ProjectHandoffClaimResponse {
    status?: string;
    claimed?: boolean;
    project_id?: string;
    fallback_redirect?: string | null;
}

interface BuildPasswordlessCallbackUrlOptions {
    otpType?: PasswordlessEmailType;
    authMode?: 'signin' | 'signup';
    redirectTo?: string;
    funnelContext?: PartialFunnelContext;
    callbackBaseUrl?: string;
}

interface SendPasswordlessEmailCodeOptions extends BuildPasswordlessCallbackUrlOptions {
    email: string;
    data?: Record<string, unknown>;
    shouldCreateUser?: boolean;
}

interface ResolvePasswordlessAuthOutcomeOptions {
    authType?: string;
    session?: Session | null;
    redirectTo?: string;
    funnelContext?: PartialFunnelContext;
    deferProfileCompletion?: boolean;
}

function buildExactActivationDestination(
    redirectTo: string | undefined,
    projectId: string
): string {
    const normalizedRedirect = normalizePostAuthRedirect(redirectTo);
    const activationPath = buildDashboardPath(normalizedRedirect, `/${projectId}/activate`);
    const [redirectPath] = normalizedRedirect.split('?');

    // Preserve non-capability query state such as setup_source when the email
    // already points at this exact activation route. Never carry query state
    // across from another project or route.
    return redirectPath === activationPath ? normalizedRedirect : activationPath;
}

function getDefaultCallbackBaseUrl(): string {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
        return `${window.location.origin}/auth`;
    }

    return 'paperzilla://auth';
}

export function buildPasswordlessCallbackUrl({
    otpType = 'email',
    authMode,
    redirectTo,
    funnelContext,
    callbackBaseUrl,
}: BuildPasswordlessCallbackUrlOptions): string {
    const baseUrl = callbackBaseUrl || getDefaultCallbackBaseUrl();
    const callbackUrl = appendQueryParams(baseUrl, {
        type: otpType,
        auth_mode: authMode,
    });

    return withFunnelAuthParams(callbackUrl, redirectTo, funnelContext);
}

export function normalizePasswordlessAuthType(type?: string): PasswordlessAuthType | 'other' {
    if (type === 'email_change') {
        return 'email_change';
    }

    if (type === 'signup' || type === 'email') {
        return type;
    }

    return 'other';
}

export async function persistPasswordlessSessionState(session?: Session | null): Promise<void> {
    const email = session?.user?.email;
    if (!email) {
        return;
    }

    await markAsLoggedIn();
    await saveLastEmail(email);
}

export async function sendPasswordlessEmailCode({
    email,
    data,
    shouldCreateUser,
    ...callbackOptions
}: SendPasswordlessEmailCodeOptions): Promise<AuthError | null> {
    const emailRedirectTo = buildPasswordlessCallbackUrl(callbackOptions);
    const explicitPreferredLanguageTag = typeof data?.preferred_language_tag === 'string'
        ? data.preferred_language_tag
        : undefined;
    const authData = {
        ...data,
        ...getAuthMessageLocaleMetadata(explicitPreferredLanguageTag),
    };
    const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
            emailRedirectTo,
            data: authData,
            shouldCreateUser,
        }
    });

    return error;
}

export async function resendPasswordlessEmailCode({
    email,
    otpType = 'email',
    shouldCreateUser,
    ...callbackOptions
}: Omit<SendPasswordlessEmailCodeOptions, 'data'>): Promise<AuthError | null> {
    const emailRedirectTo = buildPasswordlessCallbackUrl({
        otpType,
        ...callbackOptions,
    });

    if (otpType === 'email_change') {
        const { data: sessionData } = await supabase.auth.getSession();
        const currentLocale = sessionData.session?.user?.user_metadata
            ?.paperzilla_auth_message_locale;
        const localeMetadata = getAuthMessageLocaleMetadata(undefined, currentLocale);
        if (
            sessionData.session
            && currentLocale !== localeMetadata.paperzilla_auth_message_locale
        ) {
            const { error: metadataError } = await supabase.auth.updateUser({
                data: localeMetadata,
            });
            if (metadataError) {
                console.warn(
                    '[passwordlessEmail] Could not synchronize Auth email locale; the template will use its English fallback.'
                );
            }
        }

        const { error } = await supabase.auth.resend({
            type: 'email_change',
            email,
            options: {
                emailRedirectTo,
            }
        });

        return error;
    }

    const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
            emailRedirectTo,
            data: getAuthMessageLocaleMetadata(),
            shouldCreateUser,
        }
    });

    return error;
}

export async function resolvePasswordlessAuthOutcome({
    authType,
    session,
    redirectTo,
    funnelContext,
    deferProfileCompletion = false,
}: ResolvePasswordlessAuthOutcomeOptions): Promise<PasswordlessAuthOutcome> {
    const normalizedType = normalizePasswordlessAuthType(authType);

    switch (normalizedType) {
        case 'signup':
        case 'email': {
            const sessionForProfileCompletion = session ?? null;
            const resolvedDestination = await resolveProjectHandoffDestination(
                'email',
                redirectTo,
                funnelContext
            );
            if (deferProfileCompletion) {
                return {
                    destination: resolvedDestination,
                    needsProfileCompletion: false,
                    confirmationMessage: {
                        type: 'email_confirmed',
                        title: msg({ id: 'auth.confirmation.welcomeBack', message: 'Welcome back!' }),
                        message: msg({ id: 'auth.confirmation.signedIn', message: "You're now signed in. No more passwords needed!" })
                    },
                    trackingProjectId: funnelContext?.projectId,
                };
            }
            const profileDestination = resolveProfileCompletionDestination({
                session: sessionForProfileCompletion,
                destination: resolvedDestination,
                funnelContext,
            });

            if (profileDestination.needsProfileCompletion) {
                return {
                    destination: profileDestination.destination,
                    needsProfileCompletion: true,
                    trackingProjectId: funnelContext?.projectId,
                };
            }

            return {
                destination: resolvedDestination,
                needsProfileCompletion: false,
                confirmationMessage: {
                    type: 'email_confirmed',
                    title: msg({ id: 'auth.confirmation.welcomeBack', message: 'Welcome back!' }),
                    message: msg({ id: 'auth.confirmation.signedIn', message: "You're now signed in. No more passwords needed!" })
                },
                trackingProjectId: funnelContext?.projectId,
            };
        }

        case 'email_change': {
            const projectId = session?.user?.user_metadata?.project_id || funnelContext?.projectId;
            if (funnelContext?.intentId && projectId) {
                return {
                    destination: buildDashboardPath(redirectTo, `/${projectId}/activate`),
                    needsProfileCompletion: false,
                    trackingProjectId: projectId,
                };
            }
            await markProjectPendingActivation(projectId);

            return {
                destination: normalizePostAuthRedirect(
                    redirectTo || (projectId ? `/digests/${projectId}` : undefined)
                ),
                needsProfileCompletion: false,
                confirmationMessage: {
                    type: 'account_created',
                    title: msg({ id: 'auth.confirmation.welcome', message: 'Welcome to Paperzilla!' }),
                    message: msg({ id: 'auth.confirmation.accountCreated', message: 'Your account has been created. Loading your digest...' })
                },
                trackingProjectId: projectId,
            };
        }

        default:
            return {
                destination: normalizePostAuthRedirect(redirectTo),
                needsProfileCompletion: false,
                trackingProjectId: funnelContext?.projectId,
            };
    }
}

export async function resolveProjectHandoffDestination(
    otpType: PasswordlessEmailType,
    redirectTo?: string,
    funnelContext?: PartialFunnelContext
): Promise<string> {
    const fallbackDestination = normalizePostAuthRedirect(redirectTo);
    const projectId = funnelContext?.projectId;
    const intentId = funnelContext?.intentId;
    const activationGeneration = funnelContext?.activationGeneration;
    const finalizationRequestId = funnelContext?.finalizationRequestId;
    const isActivationHandoff = funnelContext?.step === 'activation'
        && Boolean(projectId)
        && Boolean(intentId)
        && Boolean(activationGeneration);
    const isDeliveryHandoff = funnelContext?.step === 'delivery' && Boolean(projectId);
    const shouldAttemptClaim = otpType === 'email';

    if (
        isActivationHandoff
        && projectId
        && intentId
        && activationGeneration
        && shouldAttemptClaim
    ) {
        // The activation page is the only owner of exact finalization and its
        // post-finalization navigation. Preserve the full binding so a magic
        // link follows the same guarded path as an in-page OTP.
        return appendQueryParams(
            buildExactActivationDestination(redirectTo, projectId),
            {
                [FUNNEL_QUERY_KEYS.source]: funnelContext?.source,
                [FUNNEL_QUERY_KEYS.step]: 'activation',
                [FUNNEL_QUERY_KEYS.projectId]: projectId,
                [FUNNEL_QUERY_KEYS.intentId]: intentId,
                [FUNNEL_QUERY_KEYS.activationGeneration]: activationGeneration.toString(),
                [FUNNEL_QUERY_KEYS.finalizationRequestId]: finalizationRequestId,
            }
        );
    }

    if (intentId) {
        return projectId
            ? appendQueryParams(
                buildDashboardPath(redirectTo, `/${projectId}/activate`),
                {
                    [FUNNEL_QUERY_KEYS.source]: funnelContext?.source,
                    [FUNNEL_QUERY_KEYS.step]: 'activation',
                    [FUNNEL_QUERY_KEYS.projectId]: projectId,
                    [FUNNEL_QUERY_KEYS.intentId]: intentId,
                    [FUNNEL_QUERY_KEYS.activationGeneration]: activationGeneration?.toString(),
                    [FUNNEL_QUERY_KEYS.finalizationRequestId]: finalizationRequestId,
                }
            )
            : fallbackDestination;
    }

    if (!isDeliveryHandoff || !projectId || !shouldAttemptClaim) {
        return shouldAttemptClaim && !projectId
            ? resumptionEntryDestination(fallbackDestination)
            : fallbackDestination;
    }

    const processingState = funnelContext?.processingState;
    const projectDestination = processingState === 'active' || processingState === 'stalled'
        ? buildDashboardPath(redirectTo, `/${projectId}/settings?onboarding=preparing`)
        : buildDashboardPath(redirectTo, `/${projectId}`);

    try {
        const { data, error } = await supabase.rpc(
            'claim_project_handoff' as any,
            { p_project_id: projectId } as any
        );

        if (error) {
            console.warn('⚠️ [PasswordlessEmail] claim_project_handoff RPC failed:', error);
            return fallbackDestination;
        }

        const handoffResult = data as ProjectHandoffClaimResponse | null;
        const handoffStatus = handoffResult?.status || 'unknown';
        console.log('🔁 [PasswordlessEmail] claim_project_handoff status:', {
            otpType,
            projectId,
            handoffStatus,
        });

        if (
            handoffStatus === 'claimed' ||
            handoffStatus === 'already_claimed' ||
            handoffStatus === 'already_owned'
        ) {
            return projectDestination;
        }

        const fallbackRedirect = handoffResult?.fallback_redirect || undefined;
        if (fallbackRedirect) {
            return normalizePostAuthRedirect(fallbackRedirect);
        }

        return fallbackDestination;
    } catch (error) {
        console.warn('⚠️ [PasswordlessEmail] claim_project_handoff threw unexpectedly:', error);
        return fallbackDestination;
    }
}

export async function markProjectPendingActivation(projectId?: string): Promise<void> {
    if (!projectId) {
        return;
    }

    const { error } = await supabase
        .from('project')
        .update({ pending_activation: true } as any)
        .eq('id', projectId)
        .eq('mode', 'experimental')
        .is('deleted_at', null);

    if (error) {
        console.error('❌ [PasswordlessEmail] Failed to set pending_activation flag:', error);
    } else {
        console.log('✅ [PasswordlessEmail] Set pending_activation flag in database for project:', projectId);
    }
}
